import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { API_URL } from '../api/config';
import { isStaffRole } from '../constants/rbac';

// User interface
interface User {
    id: number;
    full_name: string;
    email: string;
    role: 'super_admin' | 'owner' | 'project_manager' | 'tenant_admin' | 'business_manager' | 'field_manager' | 'field_scout' | 'agronomist_advisor' | 'agronomist' | 'field_facilitator' | 'finance_admin' | 'content_manager' | 'viewer';
    tenant_id?: number | null;
    tenant?: any;
    tenant_features?: Record<string, { enabled?: boolean; is_enabled?: boolean }>;
    tenant_pages?: Record<string, { enabled?: boolean; is_enabled?: boolean }>;
    language: string;
    preferences?: any;
}

export interface WorkspaceMembership {
    id?: number;
    tenant_id: number;
    tenant_name: string;
    tenant_slug?: string;
    country?: string;
    default_currency?: string;
    role: string;
    status: string;
    last_active_at?: string | null;
    accepted_at?: string | null;
}

export interface VersionInfo {
    app_name?: string;
    backend_version?: string;
    frontend_version?: string;
    git_commit?: string;
    environment?: string;
    api_uptime_seconds?: number;
}

export const VERSION_INFO: VersionInfo = { app_name: 'eSanza', backend_version: '1.0.0', frontend_version: '1.0.0', git_commit: 'unknown', environment: 'development' };

const normalizeUser = (raw: any): User => ({
    ...raw,
    role: String(raw?.role || '').trim().toLowerCase().replace(/[\s-]+/g, '_'),
});

// Login response interface


// Pages that are never governed by workspace entitlement. Login, profile,
// workspace switching, security and support must keep working even when every
// operational module is off.
const CORE_PAGES = new Set([
    'account', 'profile', 'workspace', 'workspace-selection', 'security', 'support', 'notifications', 'onboarding',
]);

// Auth context interface
interface AuthContextType {
    user: User | null;
    token: string | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    // Effective access. A missing or unknown key is DENIED — the old `!== false`
    // checks made every typo a grant.
    hasFeature: (featureKey?: string | null) => boolean;
    canAccessPage: (pageKey?: string | null) => boolean;
    accessReady: boolean;
    refreshAccess: () => Promise<void>;
    hasAnyOperationalModule: boolean;
    login: (email: string, password: string) => Promise<{ otpRequired?: boolean; challengeToken?: string; emailHint?: string; message?: string } | void>;
    requestLoginOtp: (identifier: string) => Promise<{ challengeToken?: string; identifierHint?: string; message?: string }>;
    verifyLoginOtp: (challengeToken: string, otp: string) => Promise<any>;
    selectWorkspace: (workspaceToken: string, tenantId: number) => Promise<void>;
    switchWorkspace: (tenantId: number) => Promise<void>;
    getMemberships: () => Promise<WorkspaceMembership[]>;
    logout: () => void;
    updateProfile: (updates: Partial<User>) => Promise<void>;
    changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Provider Component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    // Initialize auth state from localStorage
    useEffect(() => {
        const loadVersionInfo = async () => {
            try {
                const response = await fetch(`${API_URL}/api/version`);
                if (response.ok) {
                    const data = await response.json();
                    Object.assign(VERSION_INFO, data);
                }
            } catch {
                // version info is optional
            }
        };
        loadVersionInfo();

        const initializeAuth = async () => {
            try {
                const storedToken = localStorage.getItem('aaywa_token');
                const cachedUser = localStorage.getItem('aaywa_user');

                if (storedToken) {
                    // Decode JWT to check expiration
                    const payload = JSON.parse(atob(storedToken.split('.')[1]));
                    const isExpired = payload.exp * 1000 < Date.now();

                    if (isExpired) {
                        // Token expired, clear it
                        localStorage.removeItem('aaywa_token');
                        setToken(null);
                        setUser(null);
                    } else {
                        // Token valid, set state and fetch full profile
                        setToken(storedToken);
                        if (cachedUser) {
                            try {
                                const parsed = normalizeUser(JSON.parse(cachedUser));
                                if (isStaffRole(parsed?.role)) {
                                    setUser(parsed);
                                }
                            } catch {
                                // ignore cache parse errors
                            }
                        }
                        await fetchUserProfile(storedToken);
                    }
                }
            } catch (error) {
                console.error('Auth initialization error:', error);
                localStorage.removeItem('aaywa_token');
                localStorage.removeItem('aaywa_user');
            } finally {
                setIsLoading(false);
            }
        };

        initializeAuth();
    }, []);

    // Fetch user profile from API
    const fetchUserProfile = async (authToken: string) => {
        try {
            const response = await fetch(`${API_URL}/api/auth/me`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                const normalized = normalizeUser(data.user);
                if (!isStaffRole(normalized?.role)) {
                    localStorage.removeItem('aaywa_token');
                    localStorage.removeItem('aaywa_user');
                    setToken(null);
                    setUser(null);
                    return;
                }
                setUser(normalized);
                localStorage.setItem('aaywa_user', JSON.stringify(normalized));
            } else if (response.status === 401 || response.status === 403) {
                // Invalid or expired token, clear auth
                localStorage.removeItem('aaywa_token');
                localStorage.removeItem('aaywa_user');
                setToken(null);
                setUser(null);
            } else {
                console.warn(`Profile fetch failed with status: ${response.status}`);
            }
        } catch (error) {
            console.error('Failed to fetch profile:', error);
        }
    };

    // Login with Email/Password
    const completeLogin = (data: any) => {
        const normalized = normalizeUser(data.user);
        if (!isStaffRole(normalized?.role)) {
            throw new Error('Only staff accounts can access this dashboard.');
        }

        localStorage.setItem('aaywa_token', data.token);
        localStorage.setItem('aaywa_user', JSON.stringify(normalized));
        setToken(data.token);
        setUser(normalized);
        navigate('/dashboard');
    };

    const login = async (email: string, password: string) => {
        try {
            const response = await fetch(`${API_URL}/api/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, password })
            });

            const data = await response.json();

            if (response.ok) {
                if (data.otp_required) {
                    return {
                        otpRequired: true,
                        challengeToken: data.challengeToken,
                        emailHint: data.email_hint,
                        message: data.message,
                    };
                }
                completeLogin(data);
            } else {
                throw new Error(data.message || 'Login failed');
            }
        } catch (error: any) {
            throw new Error(error.message || 'Network error. Please try again.');
        }
    };

    const requestLoginOtp = async (identifier: string) => {
        try {
            const response = await fetch(`${API_URL}/api/auth/request-login-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ identifier })
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to send verification code');
            }
            return {
                challengeToken: data.challengeToken,
                identifierHint: data.identifier_hint,
                message: data.message,
            };
        } catch (error: any) {
            throw new Error(error.message || 'Network error. Please try again.');
        }
    };

    const verifyLoginOtp = async (challengeToken: string, otp: string) => {
        try {
            const response = await fetch(`${API_URL}/api/auth/verify-login-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ challengeToken, otp })
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Verification failed');
            }

            // Check if workspace selection is required (multi-tenant)
            if (data.requiresWorkspaceSelection) {
                return {
                    requiresWorkspaceSelection: true,
                    workspaceToken: data.workspaceToken,
                    user: data.user,
                    memberships: data.memberships,
                };
            }

            completeLogin(data);
        } catch (error: any) {
            throw new Error(error.message || 'Verification failed. Please try again.');
        }
    };

    // Select workspace for multi-tenant users
    const selectWorkspace = async (workspaceToken: string, tenantId: number) => {
        try {
            const response = await fetch(`${API_URL}/api/auth/select-workspace`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ workspaceToken, tenantId })
            });
            const data = await response.json();
            if (!response.ok) {
                if (response.status === 429) {
                    throw new Error(data.message || 'Too many workspace switch attempts. Please wait a few seconds and try again.');
                }
                throw new Error(data.message || 'Workspace selection failed');
            }
            completeLogin(data);
        } catch (error: any) {
            throw new Error(error.message || 'Workspace selection failed. Please try again.');
        }
    };

    const switchWorkspace = async (tenantId: number) => {
        if (!token) throw new Error('Not authenticated');

        try {
            const response = await fetch(`${API_URL}/api/auth/select-workspace`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ tenantId })
            });
            const data = await response.json();
            if (!response.ok) {
                if (response.status === 429) {
                    throw new Error(data.message || 'Too many workspace switch attempts. Please wait a few seconds and try again.');
                }
                throw new Error(data.message || 'Workspace switch failed');
            }
            completeLogin(data);
        } catch (error: any) {
            throw new Error(error.message || 'Workspace switch failed. Please try again.');
        }
    };

    const getMemberships = useCallback(async () => {
        if (!token) return [];

        try {
            const response = await fetch(`${API_URL}/api/auth/memberships`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to load workspaces');
            }
            return Array.isArray(data.memberships) ? data.memberships : [];
        } catch (error: any) {
            throw new Error(error.message || 'Failed to load workspaces');
        }
    }, [token]);

    // Update user profile
    const updateProfile = async (updates: Partial<User>) => {
        if (!token) throw new Error('Not authenticated');

        try {
            const response = await fetch(`${API_URL}/api/auth/profile`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(updates)
            });

            const data = await response.json();

            if (response.ok) {
                const normalized = normalizeUser(data.user);
                setUser(normalized);
                localStorage.setItem('aaywa_user', JSON.stringify(normalized));
            } else {
                throw new Error(data.message || 'Failed to update profile');
            }
        } catch (error: any) {
            throw new Error(error.message || 'Update failed');
        }
    };

    // Logout
    const logout = () => {
        if (token) {
            fetch(`${API_URL}/api/auth/logout`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }).catch(err => console.error('Logout error:', err));
        }

        localStorage.removeItem('aaywa_token');
        localStorage.removeItem('aaywa_user');
        setToken(null);
        setUser(null);
        navigate('/login');
    };

    // Change password
    const changePassword = async (oldPassword: string, newPassword: string) => {
        if (!token) throw new Error('Not authenticated');

        try {
            const response = await fetch(`${API_URL}/api/auth/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ old_password: oldPassword, new_password: newPassword })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to change password');
            }
        } catch (error: any) {
            throw new Error(error.message || 'Password change failed');
        }
    };

    const isSuperAdmin = user?.role === 'super_admin';

    const hasFeature = useCallback((featureKey?: string | null) => {
        if (!featureKey) return true;
        if (isSuperAdmin) return true;
        if (!user) return false;
        return user.tenant_features?.[featureKey]?.enabled === true;
    }, [user, isSuperAdmin]);

    const canAccessPage = useCallback((pageKey?: string | null) => {
        if (!pageKey) return true;
        if (CORE_PAGES.has(pageKey)) return true;
        if (isSuperAdmin) return true;
        if (!user) return false;
        return user.tenant_pages?.[pageKey]?.enabled === true;
    }, [user, isSuperAdmin]);

    // Access is only "ready" once we hold a real entitlement snapshot. Pages
    // must render a stable shell until then instead of assuming everything is
    // allowed during load.
    const accessReady = !!user && (isSuperAdmin || user.tenant_features !== undefined);

    const hasAnyOperationalModule = isSuperAdmin || Object.values(user?.tenant_features || {})
        .some((entry) => entry?.enabled === true);

    const refreshAccess = useCallback(async () => {
        if (!token) return;
        await fetchUserProfile(token);
    }, [token]);

    const value: AuthContextType = {
        user,
        token,
        isLoading,
        isAuthenticated: !!user && !!token,
        hasFeature,
        canAccessPage,
        accessReady,
        refreshAccess,
        hasAnyOperationalModule,
        login,
        requestLoginOtp,
        verifyLoginOtp,
        selectWorkspace,
        switchWorkspace,
        getMemberships,
        logout,
        updateProfile,
        changePassword
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use auth context
export const useAuth = () => {
    const context = useContext(AuthContext);

    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
};

export default AuthContext;
